import React, { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useRecoilValue } from 'recoil';
import {
    Box, Button, DialogActions,
    DialogContent, IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { ModalActionProps } from 'models/AutoModals';
import { CompanyType } from 'models/CompanyModel';
import { CarModel, InnerCargo, StockStatus } from 'models/StockModel';
import auth from 'recoil/atoms/auth';
import { removeItemAtIndex, replaceItemAtIndex } from 'utils/common';


type InnerCargoDialogProps = {
    car: CarModel,
} & ModalActionProps<InnerCargo[]>

const InnerCargoDialog = ({car, onFinish}: InnerCargoDialogProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const editable = my.permissions.has('cars update cargo') && (car.status === StockStatus.Pending || my.type === CompanyType.Master);
    const [state, setState] = useState(editable ? [...car.innerCargo, {} as InnerCargo] : car.innerCargo);

    const change = (idx: number, name: string, value: string | number) =>
        setState(old => replaceItemAtIndex(old, idx, {...old[idx], [name]: value}));

    const add = () => setState(old => [...old, {} as InnerCargo]);
    const remove = (idx: number) => setState(old => removeItemAtIndex(old, idx));

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onFinish!(state);
    };

    const notes = car.albums
        .filter(a => (a.name === 'yard_note' || a.name === 'yard_cargo') && a.note)
        .map(a => `${a.note}\n`);

    const inputs: Record<string, (a: InnerCargo, i: number) => ReactNode> = {
        name: (a, i) => <TextField
            id="name"
            fullWidth
            required
            variant="standard"
            value={a.name || ''}
            onChange={e => change(i, 'name', e.target.value)}
            label={isMobile ? t('name') : undefined}
            inputProps={{list: 'cargo-names'}}
        />,
        quantity: (a, i) => <TextField
            id="quantity"
            fullWidth
            required
            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
            value={a.quantity || ''}
            onChange={e => change(i, 'quantity', e.target.value)}
            label={isMobile ? t('quantity') : undefined}
        />,
        weight: (a, i) => <TextField
            id="weight"
            fullWidth
            inputProps={{inputMode: 'numeric', pattern: '[0-9.]*'}}
            value={a.weight || ''}
            onChange={e => change(i, 'weight', e.target.value)}
            label={isMobile ? t('weight') : undefined}
        />,
        price: (a, i) => <TextField
            id="price"
            fullWidth
            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
            value={a.price || ''}
            onChange={e => change(i, 'price', e.target.value)}
            label={isMobile ? t('price') : undefined}
        />,
        description: (a, i) => <TextField
            id="description"
            fullWidth
            value={a.description || ''}
            onChange={e => change(i, 'description', e.target.value)}
            label={isMobile ? t('description') : undefined}
        />,
        remove: (a, i) => <IconButton
            aria-label="delete"
            size="small"
            onClick={() => remove(i)}
        >
            <Delete/>
        </IconButton>,
    };

    return (
        <Box component="form" onSubmit={submit}>
            {notes.length > 0 && (
                <DialogContent sx={{whiteSpace: 'pre-wrap'}}>
                    {notes}
                </DialogContent>
            )}

            <datalist id="cargo-names">
                <option value="AWT"/>
                <option value="SWT"/>
                <option value="CHARGER"/>
            </datalist>

            <DialogContent>
                <TableContainer>
                    <Table
                        aria-label="Inner cargo table"
                        size="small"
                        sx={{
                            '.MuiTableCell-sizeSmall': {
                                padding: '0 4px',
                                borderBottom: 'none',
                            }
                        }}
                    >
                        {!isMobile && (
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('item_name')} *</TableCell>
                                    <TableCell>{t('quantity')} *</TableCell>
                                    <TableCell>{t('weight')}, kg</TableCell>
                                    <TableCell>{t('price')}, ¥</TableCell>
                                    <TableCell>{t('description')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                        )}

                        <TableBody>
                            {isMobile ? (
                                state.map((a, i) => (
                                    <Fragment key={`${i}${a.id}`}>
                                        <TableRow>
                                            <TableCell
                                                sx={{pt: '8px !important'}}
                                            >
                                                {inputs.name(a, i)}
                                            </TableCell>
                                            <TableCell
                                                colSpan={3}
                                                sx={{pt: '8px !important'}}
                                            >
                                                {inputs.description(a, i)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                {inputs.quantity(a, i)}
                                            </TableCell>

                                            <TableCell>
                                                {inputs.weight(a, i)}
                                            </TableCell>

                                            <TableCell>
                                                {inputs.price(a, i)}
                                            </TableCell>

                                            <TableCell>
                                                {editable && inputs.remove(a, i)}
                                            </TableCell>
                                        </TableRow>
                                    </Fragment>
                                ))
                            ) : (
                                state.map((a, i) => (
                                    <TableRow
                                        key={`${i}${a.id}`}
                                        sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                    >
                                        <TableCell>
                                            {inputs.name(a, i)}
                                        </TableCell>

                                        <TableCell>
                                            {inputs.quantity(a, i)}
                                        </TableCell>

                                        <TableCell>
                                            {inputs.weight(a, i)}
                                        </TableCell>

                                        <TableCell>
                                            {inputs.price(a, i)}
                                        </TableCell>

                                        <TableCell>
                                            {inputs.description(a, i)}
                                        </TableCell>

                                        <TableCell>
                                            {editable && inputs.remove(a, i)}
                                        </TableCell>
                                    </TableRow>
                                )))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {editable && (
                    <Box textAlign="right">
                        <IconButton
                            aria-label="add"
                            size="small"
                            onClick={add}
                        >
                            <Add/>
                        </IconButton>
                    </Box>
                )}
            </DialogContent>

            {editable && (
                <DialogActions>
                    <Button type="submit">{t('save')}</Button>
                </DialogActions>
            )}
        </Box>
    );
};

export default InnerCargoDialog;
